import React, { useCallback, useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import _get from 'lodash/get';

import {
    Button,
    Input,
    IconLock,
    LoadingEllipses,
    ValidatedInput,
} from '@trqk/components';
import { locale, getWebpackEnvVar } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { validateInputs } from '@trqk/hooks';

import { useToast } from '../../../../../hooks';
import { inputIds } from './constants';

const { CHANGE_PASSWORD_WITH_COGNITO } = queryStrings;
const labelClasses = 'font-semibold text-sm text-tertiary-300 mt-4';

interface ChangePasswordErrorCodeMap {
    NotAuthorizedException: string;
    LimitExceededException: string;
}

const errorCodeMap: ChangePasswordErrorCodeMap = {
    NotAuthorizedException:
        locale.enUS.ACCOUNT.TOASTS.CHANGE_PASSWORD_ERROR_WRONG_CURRENT_PASSWORD,
    LimitExceededException:
        locale.enUS.ACCOUNT.TOASTS.CHANGE_PASSWORD_ERROR_TOO_MANY_ATTEMPTS,
};

/**
 * Contents for a modal that allows the user to change their password. Uses input validation to
 * ensure that the user is submitting a new password that meets Cognito requirements and is not
 * the same as their current password.
 * @param props {ModalContentProps}
 */
export const ChangePasswordModal = ({
    remove,
    input,
}: ModalContentProps<{ currentUser: User }>): JSX.Element => {
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordWasEdited, setConfirmPasswordWasEdited] =
        useState(false);
    const [didSubmit, setDidSubmit] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const toast = useToast();
    const currentUser = input?.currentUser ?? null;
    const [changePassword, changePasswordQuery] = useMutation(
        CHANGE_PASSWORD_WITH_COGNITO,
        {
            onCompleted: () => {
                toast.add({
                    kind: 'info-banner',
                    content:
                        locale.enUS.ACCOUNT.TOASTS.CHANGE_PASSWORD_SUCCEEDED,
                });
                remove({
                    canceled: false,
                    success: true,
                });
            },
            onError: error => {
                let toastContent =
                    locale.enUS.ACCOUNT.TOASTS.CHANGE_PASSWORD_ERROR_GENERAL;

                const code = _get(error, [
                    'networkError',
                    'code',
                ]) as keyof ChangePasswordErrorCodeMap;

                if (code && errorCodeMap[code]) {
                    toastContent = errorCodeMap[code];
                }

                toast.add({
                    kind: 'warning-banner',
                    content: toastContent,
                });
            },
        },
    );

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.changePasswordNewPassword,
                    label: 'Password',
                    type: 'password',
                    value: newPassword,
                    required: true,
                },
            ]),
        [newPassword],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate]);

    const handleSubmit = (): void => {
        setDidSubmit(true);

        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            changePassword({
                variables: {
                    previousPassword: currentPassword,
                    proposedPassword: newPassword,
                    userPoolId,
                    clientId,
                    username: currentUser?.username,
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    const { loading } = changePasswordQuery;

    const showMismatchedPasswordWarning =
        confirmPasswordWasEdited && newPassword !== confirmPassword;
    const showDuplicatePasswordWarning =
        confirmPasswordWasEdited &&
        newPassword === confirmPassword &&
        currentPassword === confirmPassword;
    const disableSubmit =
        currentUser === null ||
        showMismatchedPasswordWarning ||
        showDuplicatePasswordWarning;

    return (
        <div
            className="px-12 py-10 bg-white rounded-lg border-l-4 border-secondary-500"
            style={{ width: 450 }}
        >
            <form>
                <div className="flex items-center mb-10">
                    <IconLock fillColor="secondary-500" width={35} />
                    <h5 className="ml-2">
                        {locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD.TITLE}
                    </h5>
                </div>
                {/*
                    This hidden input improves the UX for users with password managers:
                    https://www.chromium.org/developers/design-documents/create-amazing-password-forms
                */}
                <input
                    className="hidden"
                    autoComplete="username"
                    value={currentUser?.username ?? ''}
                    readOnly={true}
                />
                <div className={labelClasses}>
                    {
                        locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD.INPUTS
                            .CURRENT_PASSWORD
                    }
                </div>
                <Input
                    addClasses="my-3 border border-tertiary-200"
                    autoComplete="current-password"
                    inputId={inputIds.changePasswordCurrentPassword}
                    icon="password"
                    type="password"
                    value={currentPassword}
                    handleChange={setCurrentPassword}
                />
                <div className={labelClasses}>
                    {
                        locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD.INPUTS
                            .NEW_PASSWORD
                    }
                </div>
                <ValidatedInput
                    addClasses="my-3 border border-tertiary-200"
                    autoComplete="new-password"
                    inputId={inputIds.changePasswordNewPassword}
                    icon="password"
                    type="password"
                    value={newPassword}
                    handleChange={setNewPassword}
                    validationOutputs={validationOutputs}
                />
                <div className={labelClasses}>
                    {
                        locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD.INPUTS
                            .CONFIRM_PASSWORD
                    }
                </div>
                <Input
                    addClasses="my-3 border border-tertiary-200"
                    autoComplete="new-password"
                    inputId={inputIds.changePasswordConfirmPassword}
                    icon="password"
                    type="password"
                    value={confirmPassword}
                    handleChange={setConfirmPassword}
                    handleBlur={(): void => {
                        setConfirmPasswordWasEdited(true);
                    }}
                />
                <div className="h-10">
                    {showMismatchedPasswordWarning && (
                        <span className="text-sm text-highlight-orange font-semibold">
                            {
                                locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD
                                    .WARNINGS.MISMATCHED_PASSWORD
                            }
                        </span>
                    )}
                    {showDuplicatePasswordWarning && (
                        <span className="text-sm text-highlight-orange font-semibold">
                            {
                                locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD
                                    .WARNINGS.DUPLICATE_PASSWORD
                            }
                        </span>
                    )}
                </div>
                <div className="flex justify-end mt-6">
                    <Button
                        color="tertiary-light-bg"
                        autoWidth={true}
                        addClasses="mr-4"
                        handleClick={(): void => {
                            remove({
                                canceled: true,
                                success: false,
                            });
                        }}
                    >
                        <div className="text-center mx-3">
                            {
                                locale.enUS.ACCOUNT.MODALS.CHANGE_PASSWORD
                                    .BUTTONS.CANCEL
                            }
                        </div>
                    </Button>
                    <Button
                        color="secondary-light-bg"
                        disabled={disableSubmit}
                        handleClick={handleSubmit}
                    >
                        <div className="flex justify-center mx-3">
                            {!loading && (
                                <div>
                                    {
                                        locale.enUS.ACCOUNT.MODALS
                                            .CHANGE_PASSWORD.BUTTONS
                                            .UPDATE_PASSWORD
                                    }
                                </div>
                            )}
                            {loading && <LoadingEllipses width={40} />}
                        </div>
                    </Button>
                </div>
            </form>
        </div>
    );
};
